// frontend/modules/ess-monitoring/views/ESSMonitoringDashboardView.tsx
import React, { useState } from 'react';
import { Card, Tabs, Table, DatePicker, Select, Button, Space, Tag, Typography, message, Modal, Tooltip } from 'antd';
import { ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface EMkopoInboundRequest {
    id: number;
    messageId: string;
    messageType: string;
    sender: string;
    status: string;
    createdAt: string;
    requestPayload: string;
    responsePayload: string;
    errorMessage: string;
}

interface EMkopoOutboundNotification {
    id: number;
    messageId: string;
    messageType: string;
    receiver: string;
    status: string;
    createdAt: string;
    notificationPayload: string;
    essResponse: string;
    errorMessage: string;
}

const formatPayload = (payload: string | null | undefined): string => {
    if (!payload) return '(empty)';
    try {
        return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
        return payload;
    }
};

const ESSMonitoringDashboardView: React.FC = () => {
    const [activeTab, setActiveTab] = useState('inbound');
    const [inboundLogs, setInboundLogs] = useState<EMkopoInboundRequest[]>([]);
    const [outboundLogs, setOutboundLogs] = useState<EMkopoOutboundNotification[]>([]);
    const [loading, setLoading] = useState(false);
    const [previewModal, setPreviewModal] = useState<{
        open: boolean;
        title: string;
        request: string | null;
        response: string | null;
    }>({ open: false, title: '', request: null, response: null });
    const [filters, setFilters] = useState({
        messageType: undefined as string | undefined,
        status: undefined as string | undefined,
        startDate: undefined as dayjs.Dayjs | undefined,
        endDate: undefined as dayjs.Dayjs | undefined,
    });

    const fetchInboundLogs = async () => {
        setLoading(true);
        try {
            const params: any = {
                limit: 100,
            };
            if (filters.messageType) params.messageType = filters.messageType;
            if (filters.status) params.status = filters.status;
            if (filters.startDate) params.startDate = filters.startDate.toISOString();
            if (filters.endDate) params.endDate = filters.endDate.toISOString();

            const response = await axios.get('/api/admin/ess-monitoring/inbound', { params });
            setInboundLogs(response.data.content || []);
        } catch (error) {
            message.error('Failed to fetch inbound logs');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchOutboundLogs = async () => {
        setLoading(true);
        try {
            const params: any = {
                limit: 100,
            };
            if (filters.messageType) params.messageType = filters.messageType;
            if (filters.status) params.status = filters.status;
            if (filters.startDate) params.startDate = filters.startDate.toISOString();
            if (filters.endDate) params.endDate = filters.endDate.toISOString();

            const response = await axios.get('/api/admin/ess-monitoring/outbound', { params });
            setOutboundLogs(response.data.content || []);
        } catch (error) {
            message.error('Failed to fetch outbound logs');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        if (activeTab === 'inbound') {
            fetchInboundLogs();
        } else {
            fetchOutboundLogs();
        }
    };

    React.useEffect(() => {
        if (activeTab === 'inbound') {
            fetchInboundLogs();
        } else {
            fetchOutboundLogs();
        }
    }, [activeTab]);

    const inboundColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Message ID',
            dataIndex: 'messageId',
            key: 'messageId',
            width: 200,
            ellipsis: true,
        },
        {
            title: 'Message Type',
            dataIndex: 'messageType',
            key: 'messageType',
            width: 200,
        },
        {
            title: 'Sender',
            dataIndex: 'sender',
            key: 'sender',
            width: 120,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => {
                const colorMap: Record<string, string> = {
                    RECEIVED: 'blue',
                    PROCESSING: 'orange',
                    COMPLETED: 'green',
                    FAILED: 'red',
                    REJECTED: 'red',
                };
                return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
            },
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: 'Error',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            width: 150,
            ellipsis: true,
            render: (error: string) => error ? <Tag color="red">{error}</Tag> : '-',
        },
        {
            title: 'Preview',
            key: 'preview',
            width: 80,
            fixed: 'right' as const,
            render: (_: any, record: EMkopoInboundRequest) => (
                <Tooltip title="View Request & Response">
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => setPreviewModal({
                            open: true,
                            title: `#${record.id} — ${record.messageType}`,
                            request: record.requestPayload,
                            response: record.responsePayload,
                        })}
                    />
                </Tooltip>
            ),
        },
    ];

    const outboundColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Message ID',
            dataIndex: 'messageId',
            key: 'messageId',
            width: 200,
            ellipsis: true,
        },
        {
            title: 'Message Type',
            dataIndex: 'messageType',
            key: 'messageType',
            width: 200,
        },
        {
            title: 'Receiver',
            dataIndex: 'receiver',
            key: 'receiver',
            width: 120,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => {
                const colorMap: Record<string, string> = {
                    PENDING: 'orange',
                    SCHEDULED: 'blue',
                    SENDING: 'cyan',
                    SENT: 'green',
                    FAILED: 'red',
                    ACKNOWLEDGED: 'purple',
                };
                return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
            },
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: 'Error',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            width: 150,
            ellipsis: true,
            render: (error: string) => error ? <Tag color="red">{error}</Tag> : '-',
        },
        {
            title: 'Preview',
            key: 'preview',
            width: 80,
            fixed: 'right' as const,
            render: (_: any, record: EMkopoOutboundNotification) => (
                <Tooltip title="View Request & Response">
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => setPreviewModal({
                            open: true,
                            title: `#${record.id} — ${record.messageType}`,
                            request: record.notificationPayload,
                            response: record.essResponse,
                        })}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>ESS Integration Monitoring</Title>
            
            <Card>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Space wrap>
                        <Select
                            placeholder="Message Type"
                            style={{ width: 200 }}
                            allowClear
                            value={filters.messageType}
                            onChange={(value) => setFilters({ ...filters, messageType: value })}
                        >
                            <Option value="LOAN_CHARGES_REQUEST">Loan Charges Request</Option>
                            <Option value="LOAN_OFFER_REQUEST">Loan Offer Request</Option>
                            <Option value="LOAN_DISBURSEMENT_NOTIFICATION">Disbursement Notification</Option>
                            <Option value="LOAN_INITIAL_APPROVAL_NOTIFICATION">Initial Approval</Option>
                            <Option value="LOAN_FINAL_APPROVAL_NOTIFICATION">Final Approval</Option>
                        </Select>
                        
                        <Select
                            placeholder="Status"
                            style={{ width: 150 }}
                            allowClear
                            value={filters.status}
                            onChange={(value) => setFilters({ ...filters, status: value })}
                        >
                            {activeTab === 'inbound' ? (
                                <>
                                    <Option value="RECEIVED">Received</Option>
                                    <Option value="PROCESSING">Processing</Option>
                                    <Option value="COMPLETED">Completed</Option>
                                    <Option value="FAILED">Failed</Option>
                                </>
                            ) : (
                                <>
                                    <Option value="PENDING">Pending</Option>
                                    <Option value="SCHEDULED">Scheduled</Option>
                                    <Option value="SENT">Sent</Option>
                                    <Option value="FAILED">Failed</Option>
                                    <Option value="ACKNOWLEDGED">Acknowledged</Option>
                                </>
                            )}
                        </Select>
                        
                        <RangePicker
                            showTime
                            value={filters.startDate ? [filters.startDate, filters.endDate] : null}
                            onChange={(dates) => {
                                if (dates && dates[0] && dates[1]) {
                                    setFilters({ ...filters, startDate: dates[0], endDate: dates[1] });
                                } else {
                                    setFilters({ ...filters, startDate: undefined, endDate: undefined });
                                }
                            }}
                        />
                        
                        <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
                            Refresh
                        </Button>
                    </Space>

                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={[
                            {
                                key: 'inbound',
                                label: `Inbound Requests (${inboundLogs.length})`,
                                children: (
                                    <Table
                                        columns={inboundColumns}
                                        dataSource={inboundLogs}
                                        rowKey="id"
                                        loading={loading}
                                        scroll={{ x: 1200 }}
                                        pagination={{
                                            pageSize: 20,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total ${total} records`,
                                        }}
                                    />
                                ),
                            },
                            {
                                key: 'outbound',
                                label: `Outbound Notifications (${outboundLogs.length})`,
                                children: (
                                    <Table
                                        columns={outboundColumns}
                                        dataSource={outboundLogs}
                                        rowKey="id"
                                        loading={loading}
                                        scroll={{ x: 1200 }}
                                        pagination={{
                                            pageSize: 20,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total ${total} records`,
                                        }}
                                    />
                                ),
                            },
                        ]}
                    />
                </Space>
            </Card>

            <Modal
                title={previewModal.title}
                open={previewModal.open}
                onCancel={() => setPreviewModal({ ...previewModal, open: false })}
                footer={null}
                width={900}
            >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                        <strong>Request Payload</strong>
                        <pre style={{
                            background: '#f5f5f5',
                            padding: '12px',
                            borderRadius: '4px',
                            maxHeight: '500px',
                            overflow: 'auto',
                            fontSize: '12px',
                            marginTop: '8px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}>
                            {formatPayload(previewModal.request)}
                        </pre>
                    </div>
                    <div>
                        <strong>Response Payload</strong>
                        <pre style={{
                            background: '#f5f5f5',
                            padding: '12px',
                            borderRadius: '4px',
                            maxHeight: '500px',
                            overflow: 'auto',
                            fontSize: '12px',
                            marginTop: '8px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}>
                            {formatPayload(previewModal.response)}
                        </pre>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ESSMonitoringDashboardView;
